import type {InfiniteData} from '@tanstack/react-query';
import {useInfiniteQuery, useMutation, useQuery} from '@tanstack/react-query';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {
  ApiError,
  EmployeeRoleLevel,
  MetricAttachRequest,
  Workstream,
  WorkstreamBasic,
  WorkstreamContinueUpdatingCandidate,
  WorkstreamContinueUpdatingResponse,
  WorkstreamCreate,
  WorkstreamEdit,
  WorkstreamEditorDocs,
  WorkstreamMetric,
  WorkstreamMetricCreate,
  WorkstreamMetricEdit,
  WorkstreamPrivacy,
  WorkstreamService,
  WorkstreamUpdate,
  WorkstreamUpdateCreate,
  WorkstreamUpdateEdit,
} from 'client';
import {OpenAPI} from 'client/core/OpenAPI';
import {request as __request} from 'client/core/request';
import {useDebouncedValue} from 'hooks/useDebounce';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useCallback, useMemo} from 'react';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';
import {NAV_TREE_QUERY_PREFIX} from 'views/workstreams/page/useNavTree';

import {
  getMetricLinkedWorkstreamIds,
  getMetricOrder,
  getMetricWorkstreamLink,
  isPrimaryMetric,
} from './utils';
import {getWorkstreamHealthScoreEnabledQueryKey} from './workstreamChecks';
import {CustomWorkstreamPrivacySubtext, useEmployeeCount} from './WorkstreamPrivacyDrawer';

type WorkstreamMetricWithCurrentLink = WorkstreamMetric & {
  workstream_metric_link?: {
    id?: number | null;
    order?: number | null;
    is_primary?: boolean | null;
  } | null;
};

type WorkstreamMetricRequestUpdate = Partial<WorkstreamMetricWithCurrentLink> & {
  touch_current_updated_at?: boolean | null;
};

/** Matches `useWorkstreamDetail` in `views/workstreams_v2/hooks.tsx` (avoid importing v2 from here). */
const WORKSTREAM_DETAIL_QUERY_PREFIX = ['workstream-data', 'workstream-detail'] as const;
const WORKSTREAM_LIST_QUERY_PREFIX = ['workstream-data', 'workstreams'] as const;
const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;
const CONTINUE_UPDATING_DEFAULT_LIMIT = 5;
const CONTINUE_UPDATING_MAX_LIMIT = 5;

const invalidateNotificationQueries = () =>
  queryClient.invalidateQueries({queryKey: NOTIFICATIONS_QUERY_KEY});

export type WorkstreamDisplayStatus = {
  archiveUpdate?: WorkstreamUpdate;
  latestUpdate?: WorkstreamUpdate;
  previousUpdate?: WorkstreamUpdate;
  showArchivedChip: boolean;
};

export const useWorkstreamDisplayStatus = (workstream: {
  updates?: WorkstreamUpdate[];
  archived?: boolean;
}): WorkstreamDisplayStatus => {
  const archived = Boolean(workstream.archived);
  const updates = workstream.updates;
  return useMemo(() => {
    const [firstUpdate] = updates ?? [];
    const archiveUpdate = firstUpdate?.status === 'archived' ? firstUpdate : undefined;
    const [latestUpdate, previousUpdate] = (updates ?? []).filter(
      (update) => update.status !== 'archived'
    );

    if (!archiveUpdate) {
      return {
        latestUpdate,
        previousUpdate,
        showArchivedChip: archived,
      };
    }

    return {archiveUpdate, latestUpdate, previousUpdate, showArchivedChip: true};
  }, [archived, updates]);
};

export const workstreamContinueUpdatingKeys = {
  all: ['workstreams', 'continue-updating'] as const,
  list: ({excludeWorkstreamId, limit}: {excludeWorkstreamId?: number | null; limit: number}) =>
    [
      ...workstreamContinueUpdatingKeys.all,
      {
        excludeWorkstreamId: excludeWorkstreamId ?? null,
        limit,
      },
    ] as const,
};

type UseContinueUpdatingCandidatesOptions = {
  excludeWorkstreamId?: number | null;
  enabled?: boolean;
  limit?: number;
};

const getCachedWorkstreamsWithDependencies = () => {
  const cachedQueryGroups = [
    queryClient.getQueriesData({queryKey: ['workstream']}),
    queryClient.getQueriesData({queryKey: [...WORKSTREAM_DETAIL_QUERY_PREFIX]}),
  ];
  const cachedWorkstreams = new Map<number, Partial<Workstream>>();

  for (const queryGroup of cachedQueryGroups) {
    for (const [, cached] of queryGroup) {
      if (!cached || typeof cached !== 'object') {
        continue;
      }

      const workstream = cached as Partial<Workstream>;
      if (typeof workstream.id !== 'number' || !Array.isArray(workstream.dependencies)) {
        continue;
      }

      cachedWorkstreams.set(workstream.id, workstream);
    }
  }

  return cachedWorkstreams;
};

const findMetricInWorkstreamTree = (
  cachedNode: unknown,
  metricId: number
): WorkstreamMetric | undefined => {
  if (!cachedNode || typeof cachedNode !== 'object') {
    return undefined;
  }

  const workstreamNode = cachedNode as Workstream & {
    dependencies?: Array<{
      dependent?: Workstream | null;
    }> | null;
  };

  if (Array.isArray(workstreamNode.metrics)) {
    const metric = workstreamNode.metrics.find((candidate) => candidate.id === metricId);
    if (metric) {
      return metric;
    }
  }

  if (!Array.isArray(workstreamNode.dependencies)) {
    return undefined;
  }

  for (const dependency of workstreamNode.dependencies) {
    const metric = findMetricInWorkstreamTree(dependency?.dependent, metricId);
    if (metric) {
      return metric;
    }
  }

  return undefined;
};

const getFreshCachedMetric = (
  workstreamId: number,
  metricId: number
): WorkstreamMetric | undefined => {
  const directQueryGroups = [
    queryClient.getQueriesData({queryKey: ['workstream', workstreamId]}),
    queryClient.getQueriesData({queryKey: ['workstream']}),
    queryClient.getQueriesData({queryKey: [...WORKSTREAM_DETAIL_QUERY_PREFIX]}),
  ];

  for (const queryGroup of directQueryGroups) {
    for (const [, cached] of queryGroup) {
      const metric = findMetricInWorkstreamTree(cached, metricId);
      if (metric) {
        return metric;
      }
    }
  }

  const paginatedListQueries = queryClient.getQueriesData({
    queryKey: WORKSTREAM_LIST_QUERY_PREFIX,
    exact: false,
  });

  for (const [, cached] of paginatedListQueries) {
    if (
      !cached ||
      typeof cached !== 'object' ||
      !Array.isArray((cached as {pages?: unknown[]}).pages)
    ) {
      continue;
    }

    const pages = (cached as {pages: Array<{workstreams?: unknown[]}>}).pages;
    for (const page of pages) {
      if (!Array.isArray(page.workstreams)) {
        continue;
      }

      for (const workstream of page.workstreams) {
        const metric = findMetricInWorkstreamTree(workstream, metricId);
        if (metric) {
          return metric;
        }
      }
    }
  }

  return undefined;
};

const resolveMetricEditBase = (
  workstreamId: number,
  metricId: number,
  existing: WorkstreamMetric
): WorkstreamMetricWithCurrentLink => {
  const cachedMetric = getFreshCachedMetric(workstreamId, metricId);
  if (!cachedMetric) {
    return existing;
  }

  const cachedScopedLink = getMetricWorkstreamLink(cachedMetric, workstreamId);
  const existingScopedLink = getMetricWorkstreamLink(existing, workstreamId);
  const resolvedScopedLink =
    cachedScopedLink ??
    existingScopedLink ??
    (cachedMetric as WorkstreamMetricWithCurrentLink).workstream_metric_link;
  const resolvedLinkCollection =
    Array.isArray(cachedMetric.workstream_metric_links) &&
    cachedMetric.workstream_metric_links.length > 0
      ? cachedMetric.workstream_metric_links
      : existing.workstream_metric_links;

  return {
    ...existing,
    ...cachedMetric,
    workstream_metric_link: resolvedScopedLink,
    workstream_metric_links: resolvedLinkCollection,
  };
};

const getAncestorWorkstreamIdsForDependency = (dependencyWorkstreamId: number) => {
  const cachedWorkstreams = getCachedWorkstreamsWithDependencies();
  const ancestorIds = new Set<number>();
  const queue = [dependencyWorkstreamId];

  while (queue.length > 0) {
    const currentDependencyId = queue.shift();
    if (currentDependencyId == null) {
      continue;
    }

    for (const [workstreamId, workstream] of cachedWorkstreams.entries()) {
      if (workstreamId === currentDependencyId || ancestorIds.has(workstreamId)) {
        continue;
      }

      if (
        workstream.dependencies?.some(
          (dependency) => dependency?.dependent?.id === currentDependencyId
        )
      ) {
        ancestorIds.add(workstreamId);
        queue.push(workstreamId);
      }
    }
  }

  return [...ancestorIds];
};

export const usePrivacyText = (
  workstream: WorkstreamEdit & {metrics?: WorkstreamMetric[]},
  type?: WorkstreamPrivacy
) => {
  const employeeCount = useEmployeeCount(
    [
      ...(workstream.employee_associations?.map(({user_id}) => user_id) ?? []),
      ...(workstream.metrics
        ?.map(({owner}) => owner?.user_id)
        .filter((userId): userId is string => Boolean(userId)) ?? []),
    ],
    workstream.private_employees ?? [],
    (type ?? workstream.privacy) === WorkstreamPrivacy.CUSTOM
  );

  const teamCount = workstream.private_teams?.length ?? 0;

  switch (type ?? workstream.privacy) {
    case WorkstreamPrivacy.PUBLIC:
      return {
        title: t('workstream_settings_privacy_public'),
        subtitle: t('workstream_settings_privacy_subtitle_public'),
      };
    case WorkstreamPrivacy.PRIVATE:
      return {
        title: t('workstream_settings_privacy_private'),
        subtitle: t('workstream_settings_privacy_subtitle_private'),
      };
    case WorkstreamPrivacy.CUSTOM:
      return {
        title: t('workstream_settings_privacy_custom'),
        subtitle:
          workstream.privacy === WorkstreamPrivacy.CUSTOM ? (
            <CustomWorkstreamPrivacySubtext employeeCount={employeeCount} teamCount={teamCount} />
          ) : (
            t('workstream_settings_privacy_subtitle_custom')
          ),
      };
    default:
      return {title: '', subtitle: ''};
  }
};

export const useWorkstreamStatus = () => {
  const invalidateWorkstreamStatusQueries = (workstreamId: number) => {
    // Trackers tree only — avoid `['workstreams', 'trackers']` (GET /workstream/trackers).
    queryClient.invalidateQueries({queryKey: ['workstreams'], exact: true});
    // workstreams_v2 table/list/detail queries live under the workstream-data namespace.
    queryClient.invalidateQueries({queryKey: ['workstream-data'], exact: false});
    queryClient.invalidateQueries({
      queryKey: ['workstream-data', 'workstream-detail', workstreamId],
    });
    queryClient.invalidateQueries({queryKey: ['workstream', workstreamId]});
    queryClient.invalidateQueries({queryKey: ['workstream', 'logs', workstreamId]});
    queryClient.invalidateQueries({queryKey: ['workstream', 'updates', workstreamId]});
    // The rail's status dots, and archived workstreams dropping out of it.
    queryClient.invalidateQueries({queryKey: NAV_TREE_QUERY_PREFIX});

    const parentWorkstreamIds = getAncestorWorkstreamIdsForDependency(workstreamId);
    for (const parentWorkstreamId of parentWorkstreamIds) {
      queryClient.invalidateQueries({queryKey: ['workstream', parentWorkstreamId]});
      queryClient.invalidateQueries({
        queryKey: [...WORKSTREAM_DETAIL_QUERY_PREFIX, parentWorkstreamId],
      });
    }
  };

  const {mutateAsync: createUpdate, isPending} = useMutation({
    mutationFn: ({ownerId, update}: {ownerId: string; update: WorkstreamUpdateCreate}) =>
      getService(WorkstreamService).statusApiV1WorkstreamUpdatePost(ownerId, update),
    onSettled: (response, _, {update}) => {
      if (!response) {
        return;
      }

      invalidateWorkstreamStatusQueries(update.workstream_id);
    },
  });

  const {mutateAsync: editUpdate, isPending: isEditing} = useMutation({
    mutationFn: ({update}: {update: WorkstreamUpdateEdit}) =>
      getService(WorkstreamService).editUpdateApiV1WorkstreamUpdatePut(update),
    onSettled: (response, _, {update}) => {
      if (!response) {
        return;
      }

      invalidateWorkstreamStatusQueries(update.workstream_id);
    },
  });

  // An empty summary resets it to AI; the next view regenerates it.
  const {mutateAsync: editUpdateSummary} = useMutation({
    mutationFn: ({id, summary}: {id: number; workstreamId: number; summary: string}) =>
      getService(WorkstreamService).editUpdateSummaryApiV1WorkstreamUpdateIdSummaryPut(id, {
        summary,
      }),
    onSettled: (_response, _error, {workstreamId}) =>
      invalidateWorkstreamStatusQueries(workstreamId),
  });

  const {mutateAsync: deleteUpdate, isPending: isDeleting} = useMutation({
    mutationFn: ({id}: {id: number; workstreamId: number}) =>
      getService(WorkstreamService).deleteUpdateApiV1WorkstreamUpdateIdDelete(id),
    onSettled: (response, _, {workstreamId}) => {
      if (!response) {
        return;
      }

      queryClient.invalidateQueries({queryKey: ['workstreams'], exact: true});
      queryClient.invalidateQueries({queryKey: ['workstream-data'], exact: false});
      // Unarchiving deletes the archive update, which brings it back to the rail.
      queryClient.invalidateQueries({queryKey: NAV_TREE_QUERY_PREFIX});
      void invalidateQueriesForWorkstreamId(workstreamId);
    },
  });

  return {
    createUpdate,
    isPending,
    editUpdate,
    isEditing,
    editUpdateSummary,
    deleteUpdate,
    isDeleting,
  };
};

const fetchWorkstream = async (
  id: number | string,
  reports?: boolean,
  delegates?: boolean
): Promise<Workstream> => {
  const response = await getService(WorkstreamService).getApiV1WorkstreamIdGet(
    id,
    reports,
    delegates
  );
  if (typeof response.health_score_enabled === 'boolean') {
    queryClient.setQueryData(
      getWorkstreamHealthScoreEnabledQueryKey(response.id),
      response.health_score_enabled
    );
  }
  return response as Workstream;
};

// Warms useWorkstream(id)'s cache (e.g. on hover, before a click needs it). A
// fetch from the last 30s counts, so repeated hovers don't refetch.
export const prefetchWorkstream = (id: number) =>
  void queryClient.prefetchQuery({
    queryKey: ['workstream', id, undefined, undefined],
    queryFn: () => fetchWorkstream(id),
    staleTime: 30_000,
  });

export const useWorkstream = (
  id?: number | string,
  reports?: boolean,
  delegates?: boolean,
  // Off for a cache warmer that's always mounted (the Navbar's): pages showing
  // the workstream still refetch it on focus.
  {refetchOnWindowFocus = 'always'}: {refetchOnWindowFocus?: 'always' | false} = {}
) => {
  const {
    data,
    isLoading,
    isPending,
    isError,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ['workstream', id, reports, delegates],
    queryFn: () => fetchWorkstream(id!, reports, delegates),
    enabled: id !== undefined && id !== 0,
    initialData: () => {
      const detailCacheId = typeof id === 'string' ? Number(id) : id;

      if (
        detailCacheId === undefined ||
        detailCacheId === 0 ||
        !Number.isFinite(detailCacheId) ||
        reports !== undefined ||
        delegates !== undefined
      ) {
        return undefined;
      }

      return queryClient.getQueryData([...WORKSTREAM_DETAIL_QUERY_PREFIX, detailCacheId]) as
        | Workstream
        | undefined;
    },
    refetchOnWindowFocus,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 404 || error.status === 422)) {
        return false;
      }
      return failureCount < 3;
    },
  });

  return {workstream: data, isLoading, isPending, isError, error: loadError, refetch};
};

export const useWorkstreamAncestors = (id?: number) => {
  const {data, isLoading} = useQuery({
    queryKey: ['workstream', 'ancestors', id],
    queryFn: async () => {
      if (id === undefined || id === 0) {
        return {ancestors: [], ancestorPaths: []};
      }
      const response =
        await getService(
          WorkstreamService
        ).getWorkstreamBreadcrumbPathsApiV1WorkstreamIdBreadcrumbPathsGet(id);
      const ancestorPaths = response.paths ?? [];

      const ancestors = Array.from(
        new Map(ancestorPaths.flat().map((ancestor) => [ancestor.id, ancestor])).values()
      );

      return {ancestors, ancestorPaths};
    },
    enabled: id !== undefined && id !== 0,
  });

  return {ancestors: data?.ancestors ?? [], ancestorPaths: data?.ancestorPaths ?? [], isLoading};
};

export const useDescriptionEditor = (id: number) => {
  const {mutateAsync: editDescription, isPending} = useMutation({
    mutationFn: (description: string) =>
      getService(WorkstreamService).descriptionApiV1WorkstreamIdDescriptionPut(id, {description}),
    onSettled: (response, _, description) => {
      if (!response) {
        return;
      }

      const updateCachedDescription = (old: unknown) =>
        old && typeof old === 'object' ? {...(old as Workstream), description} : old;

      // Optimistically update the cached workstream description
      queryClient.setQueriesData({queryKey: ['workstream', id]}, updateCachedDescription);
      queryClient.setQueryData([...WORKSTREAM_DETAIL_QUERY_PREFIX, id], updateCachedDescription);
    },
  });

  return {editDescription, isPending};
};

export const useAuditLogs = (workstreamId: number, disabled?: boolean) => {
  const {data: auditLogs, isLoading} = useQuery({
    queryKey: ['workstream', 'logs', workstreamId],
    queryFn: () => getService(WorkstreamService).getLogsApiV1WorkstreamIdLogsGet(workstreamId),
    enabled: !disabled,
  });

  const filteredAuditLogs = useMemo(() => {
    return auditLogs?.filter((log) => log.before !== log.after);
  }, [auditLogs]);

  return {data: filteredAuditLogs, isLoading};
};

export const useAllUpdates = (workstreamId: number, disabled?: boolean) => {
  const {data, isLoading} = useQuery({
    queryKey: ['workstream', 'updates', workstreamId],
    queryFn: () =>
      getService(WorkstreamService).getUpdatesApiV1WorkstreamIdUpdatesGet(workstreamId),
    enabled: !disabled,
  });

  return {data, isLoading};
};

export const useCompanyWorkstreams = ({timeframe}: {timeframe: string}) => {
  const {data: trackers, isLoading} = useQuery({
    queryKey: ['workstreams', 'company', timeframe],
    queryFn: () => getService(WorkstreamService).getCompanyApiV1WorkstreamCompanyGet(timeframe),
  });

  return {trackers, isLoading};
};

export const useTrackerOptions = ({enabled = true}: {enabled?: boolean} = {}) => {
  const {data, isLoading} = useQuery({
    queryKey: ['workstreams', 'trackers'],
    queryFn: getService(WorkstreamService).getTrackersApiV1WorkstreamTrackersGet,
    enabled,
  });

  return {data, isLoading};
};

const searchWorkstreamTypeahead = async (
  query: string,
  limit: number
): Promise<WorkstreamBasic[]> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/api/v1/workstream/search',
    query: {
      query,
      limit,
    },
    errors: {
      422: 'Validation Error',
    },
  }) as Promise<WorkstreamBasic[]>;

export const useWorkstreamTypeaheadSearch = ({
  input,
  limit = 100,
  enabled = true,
}: {
  input: string;
  limit?: number; // maximum number of results to return even if there are more results
  enabled?: boolean;
}) => {
  const debouncedInput = useDebouncedValue(input.trim(), 250);
  const {data, isLoading} = useQuery({
    queryKey: ['workstreams', 'typeahead', debouncedInput, limit],
    queryFn: () => searchWorkstreamTypeahead(debouncedInput, limit),
    enabled: enabled && debouncedInput.length > 0,
  });

  return {data, isLoading, query: debouncedInput};
};

export const useContinueUpdatingCandidates = ({
  excludeWorkstreamId = null,
  enabled = true,
  limit = CONTINUE_UPDATING_DEFAULT_LIMIT,
}: UseContinueUpdatingCandidatesOptions = {}) => {
  const effectiveLimit = Math.min(Math.max(1, Math.floor(limit)), CONTINUE_UPDATING_MAX_LIMIT);
  const query = useInfiniteQuery<
    WorkstreamContinueUpdatingResponse,
    Error,
    InfiniteData<WorkstreamContinueUpdatingResponse, string | null>,
    ReturnType<typeof workstreamContinueUpdatingKeys.list>,
    string | null
  >({
    queryKey: workstreamContinueUpdatingKeys.list({
      excludeWorkstreamId,
      limit: effectiveLimit,
    }),
    queryFn: async ({pageParam, signal}) => {
      const request = getService(
        WorkstreamService
      ).getContinueUpdatingCandidatesApiV1WorkstreamContinueUpdatingGet(
        excludeWorkstreamId ?? undefined,
        effectiveLimit,
        pageParam ?? undefined
      );
      const abortRequest = () => {
        if ('cancel' in request && typeof request.cancel === 'function') {
          request.cancel();
        }
      };
      signal?.addEventListener('abort', abortRequest, {once: true});

      try {
        return await request;
      } finally {
        signal?.removeEventListener('abort', abortRequest);
      }
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled,
  });

  const candidates = useMemo<WorkstreamContinueUpdatingCandidate[]>(() => {
    const pages = query.data?.pages;
    if (!pages || pages.length === 0) {
      return [];
    }

    const seenIds = new Set<number>();
    const items: WorkstreamContinueUpdatingCandidate[] = [];
    for (const page of pages) {
      for (const candidate of page.items) {
        if (seenIds.has(candidate.id)) {
          continue;
        }
        seenIds.add(candidate.id);
        items.push(candidate);
      }
    }
    return items;
  }, [query.data?.pages]);

  const pages = query.data?.pages;
  const lastPage = pages && pages.length > 0 ? pages[pages.length - 1] : undefined;
  const hasCandidates = candidates.length > 0;

  return {
    ...query,
    candidates,
    hasCandidates,
    nextCursor: lastPage?.next_cursor ?? null,
    shouldShowModal: query.isSuccess && hasCandidates,
  };
};

export const useWorkstreamCreate = ({archive = false}: {archive?: boolean} = {}) => {
  const {mutateAsync: createWorkstream, isPending} = useMutation({
    mutationFn: ({data}: {data: WorkstreamCreate}) =>
      getService(WorkstreamService).createApiV1WorkstreamPost(data, archive),
    onSettled: (response) => {
      if (!response) {
        return;
      }

      queryClient.invalidateQueries({queryKey: ['workstreams'], exact: true});
      queryClient.invalidateQueries({queryKey: ['workstreams', 'trackers']});
      queryClient.invalidateQueries({queryKey: ['workstreams', 'typeahead'], exact: false});
    },
  });

  return {createWorkstream, isPending};
};

/**
 * Invalidate React Query caches for a single workstream ID (logs, jira, useWorkstream variants, etc.).
 * Avoid `queryKey: ['workstream'], exact: false` — it matches every workstream* query in the app and
 * makes `mutateAsync` wait for unbounded refetches.
 */
function invalidateQueriesForWorkstreamId(workstreamId: number) {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'workstream' &&
        key[1] !== 'outcomes_validation' &&
        (key as unknown[]).includes(workstreamId)
      );
    },
  });
}

export type WorkstreamEditMutationVariables = {
  reason?: string;
  data: WorkstreamEdit;
  /**
   * When set, immediately patches cached workstreams (`useWorkstream`, v2 detail) so DRI / exec sponsor
   * UI updates before the network round trip. Rolled back automatically on mutation error.
   */
  optimisticEmployeesByRole?: Partial<Record<'dri' | 'exec_sponsor', EmployeeRoleLevel | null>>;
  /**
   * When set, immediately patches cached employee associations for the active workstream.
   * Useful for multi-person edits like working team, where waiting on refetch causes visible page churn.
   */
  optimisticEmployeeAssociations?: Workstream['employee_associations'];
};

function applyOptimisticEmployeesByRole(
  prev: Workstream,
  patch: Partial<Record<'dri' | 'exec_sponsor', EmployeeRoleLevel | null>>
): Workstream {
  const entries = Object.entries(patch) as [
    'dri' | 'exec_sponsor',
    EmployeeRoleLevel | null | undefined,
  ][];
  if (entries.length === 0) {
    return prev;
  }

  let associations = [...(prev.employee_associations ?? [])];
  for (const [roleId, employeeValue] of entries) {
    if (roleId !== 'dri' && roleId !== 'exec_sponsor') {
      continue;
    }
    associations = associations.filter((a) => a.dri !== roleId);
    if (employeeValue != null) {
      associations.push({dri: roleId, employee: employeeValue});
    }
  }
  return {...prev, employee_associations: associations};
}

export const useWorkstreamEdit = ({
  invalidateQueries = true,
  onSuccess,
}: {
  invalidateQueries?: boolean;
  onSuccess?: (workstream: Workstream) => void;
} = {}) => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const {mutateAsync: editWorkstream, isPending} = useMutation({
    mutationFn: async ({reason, data}: WorkstreamEditMutationVariables) => {
      const result = await getService(WorkstreamService).editApiV1WorkstreamPut(
        employee.user_id,
        data,
        reason
      );

      if (typeof result !== 'number' || result <= 0) {
        throw new Error(t('unable_to_save').toString());
      }

      return result;
    },
    onMutate: async (variables) => {
      const {data, optimisticEmployeesByRole, optimisticEmployeeAssociations} = variables;
      const workstreamId = data.id;
      if (
        workstreamId == null ||
        ((!optimisticEmployeesByRole || Object.keys(optimisticEmployeesByRole).length === 0) &&
          optimisticEmployeeAssociations == null)
      ) {
        return undefined;
      }

      await queryClient.cancelQueries({queryKey: ['workstream', workstreamId]});
      await queryClient.cancelQueries({
        queryKey: [...WORKSTREAM_DETAIL_QUERY_PREFIX, workstreamId],
      });

      const previousEntries: Array<[readonly unknown[], unknown]> = [];

      const queries = queryClient.getQueriesData({queryKey: ['workstream', workstreamId]});
      for (const [queryKey, cached] of queries) {
        previousEntries.push([queryKey, cached]);
        if (!cached || typeof cached !== 'object') {
          continue;
        }
        queryClient.setQueryData(queryKey, (existing: Workstream) => {
          let next = existing;
          if (optimisticEmployeesByRole && Object.keys(optimisticEmployeesByRole).length > 0) {
            next = applyOptimisticEmployeesByRole(next, optimisticEmployeesByRole);
          }
          if (optimisticEmployeeAssociations != null) {
            next = {...next, employee_associations: optimisticEmployeeAssociations};
          }
          return next;
        });
      }

      const detailKey = [...WORKSTREAM_DETAIL_QUERY_PREFIX, workstreamId] as const;
      const prevDetail = queryClient.getQueryData(detailKey);
      previousEntries.push([detailKey, prevDetail]);
      if (prevDetail && typeof prevDetail === 'object') {
        queryClient.setQueryData(detailKey, (existing: Workstream) => {
          let next = existing;
          if (optimisticEmployeesByRole && Object.keys(optimisticEmployeesByRole).length > 0) {
            next = applyOptimisticEmployeesByRole(next, optimisticEmployeesByRole);
          }
          if (optimisticEmployeeAssociations != null) {
            next = {...next, employee_associations: optimisticEmployeeAssociations};
          }
          return next;
        });
      }

      return {previousEntries};
    },
    onError: (_error, _variables, context) => {
      context?.previousEntries?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: async (data, error, variables) => {
      if (error) {
        return;
      }

      const workstreamId =
        typeof data === 'number' && data > 0
          ? data
          : typeof variables?.data?.id === 'number' && variables.data.id > 0
            ? variables.data.id
            : undefined;
      if (workstreamId == null) {
        return;
      }

      if (invalidateQueries) {
        await queryClient.invalidateQueries({queryKey: ['workstreams'], exact: true});
        await queryClient.invalidateQueries({queryKey: ['workstreams', 'typeahead'], exact: false});
        await invalidateQueriesForWorkstreamId(workstreamId);
      }
      await queryClient.invalidateQueries({
        queryKey: [...WORKSTREAM_DETAIL_QUERY_PREFIX, workstreamId],
        refetchType: 'all',
      });

      if (onSuccess) {
        await queryClient.invalidateQueries({
          queryKey: ['workstream', workstreamId, undefined, undefined],
        });
        const newWorkstream = await queryClient.getQueryData([
          'workstream',
          workstreamId,
          undefined,
          undefined,
        ]);
        onSuccess(newWorkstream as Workstream);
      }
    },
  });

  return {editWorkstream, isPending};
};

function toMetricEditBody(
  existing: WorkstreamMetric,
  update: WorkstreamMetricRequestUpdate,
  workstreamId: number
): WorkstreamMetricEdit {
  const updateWithLink = update as WorkstreamMetricRequestUpdate;
  const merged = {...existing, ...updateWithLink};
  const existingScopedLink = getMetricWorkstreamLink(existing, workstreamId);
  const mergedScopedLink =
    updateWithLink.workstream_metric_link || existingScopedLink
      ? {
          ...(existingScopedLink ?? {}),
          ...(updateWithLink.workstream_metric_link ?? {}),
          workstream_id: workstreamId,
        }
      : null;
  const nextOrder = mergedScopedLink?.order ?? getMetricOrder(existing, workstreamId);
  const nextIsPrimary = mergedScopedLink?.is_primary ?? isPrimaryMetric(existing, workstreamId);
  return {
    type: merged.type ?? 'deliverable',
    title: merged.title ?? '',
    order: undefined,
    baseline: merged.baseline ?? null,
    current: merged.current ?? null,
    target: merged.target ?? null,
    status: merged.status ?? null,
    complete: merged.complete ?? null,
    complete_date: merged.complete_date ?? null,
    url: merged.url ?? null,
    definition: merged.definition ?? null,
    is_longterm: merged.is_longterm ?? null,
    is_mnpi: merged.is_mnpi ?? null,
    metric_type: merged.metric_type ?? null,
    is_primary: undefined,
    id: merged.id ?? null,
    owner_id: merged.owner?.user_id ?? null,
    metric_forecasts: merged.metric_forecasts?.map((forecast) => ({
      id: forecast.id ?? null,
      period: forecast.period,
      budget: forecast.budget ?? null,
      target: forecast.target,
      actual: forecast.actual ?? null,
      forecast_date: forecast.forecast_date ?? null,
      owner_id: forecast.owner_id ?? null,
    })),
    workstream_metric_link: mergedScopedLink
      ? {
          id: mergedScopedLink.id ?? null,
          order: nextOrder,
          is_primary: Boolean(nextIsPrimary),
        }
      : undefined,
    touch_current_updated_at: updateWithLink.touch_current_updated_at ?? undefined,
  };
}

function toMetricCreateBody(
  update: Partial<WorkstreamMetricWithCurrentLink>,
  order: number
): WorkstreamMetricCreate {
  const updateWithLink = update as Partial<WorkstreamMetricWithCurrentLink>;
  const resolvedOrder = updateWithLink.workstream_metric_link?.order ?? order;
  const resolvedIsPrimary = Boolean(updateWithLink.workstream_metric_link?.is_primary);
  return {
    type: update.type ?? 'deliverable',
    title: (update.title ?? '') as string,
    order: undefined,
    baseline: updateWithLink.baseline ?? null,
    current: update.current ?? null,
    target: update.target ?? null,
    status: update.status ?? null,
    complete: update.complete ?? null,
    complete_date: update.complete_date ?? null,
    url: update.url ?? null,
    definition: update.definition ?? null,
    is_longterm: update.is_longterm ?? null,
    is_mnpi: update.is_mnpi ?? null,
    metric_type: update.metric_type ?? null,
    is_primary: undefined,
    owner_id: update.owner?.user_id ?? null,
    metric_forecasts: update.metric_forecasts?.map((forecast) => ({
      period: forecast.period,
      budget: forecast.budget ?? null,
      target: forecast.target,
      actual: forecast.actual ?? null,
      forecast_date: forecast.forecast_date ?? null,
    })),
    workstream_metric_link: {
      order: resolvedOrder,
      is_primary: resolvedIsPrimary,
    },
  };
}

export const useWorkstreamMetricMutation = ({
  invalidateQueries = true,
}: {
  invalidateQueries?: boolean;
} = {}) => {
  const normalizeMetricInvalidationIds = useCallback((workstreamIds: number[]) => {
    return Array.from(
      new Set(
        workstreamIds.filter((id): id is number => Number.isFinite(id) && typeof id === 'number')
      )
    ).sort((a, b) => a - b);
  }, []);

  const updateMetricInCachedWorkstream = useCallback(
    (
      workstreamIds: number[],
      originWorkstreamId: number,
      metricId: number,
      update: WorkstreamMetricRequestUpdate | WorkstreamMetricEdit
    ) => {
      const buildOptimisticMetricForWorkstream = (
        existingMetric: WorkstreamMetric,
        targetWorkstreamId: number
      ) => {
        const {workstream_metric_link: updatedScopedLink, ...sharedUpdate} = update as Partial<
          WorkstreamMetric & {
            workstream_metric_link?: {
              id?: number | null;
              workstream_id?: number | null;
              order?: number | null;
              is_primary?: boolean | null;
            } | null;
          } & {
            touch_current_updated_at?: boolean | null;
          }
        >;
        delete sharedUpdate.touch_current_updated_at;
        const targetScopedLink = getMetricWorkstreamLink(existingMetric, targetWorkstreamId);
        const nextScopedLink =
          targetWorkstreamId === originWorkstreamId
            ? targetScopedLink || updatedScopedLink
              ? {
                  ...(targetScopedLink ?? {}),
                  ...(updatedScopedLink ?? {}),
                  workstream_id: targetWorkstreamId,
                }
              : undefined
            : targetScopedLink
              ? {
                  ...targetScopedLink,
                  workstream_id: targetWorkstreamId,
                }
              : undefined;
        const existingLinkCollection = Array.isArray(existingMetric.workstream_metric_links)
          ? existingMetric.workstream_metric_links
          : undefined;
        const nextLinkCollection = existingLinkCollection
          ? (() => {
              let didUpdateTargetLink = false;
              const patchedLinks = existingLinkCollection.map((link) => {
                if (!link || link.workstream_id !== targetWorkstreamId) {
                  return link;
                }

                didUpdateTargetLink = true;
                return nextScopedLink ?? link;
              });

              if (!didUpdateTargetLink && nextScopedLink) {
                return [...patchedLinks, nextScopedLink];
              }

              return patchedLinks;
            })()
          : existingLinkCollection;

        return {
          ...existingMetric,
          ...sharedUpdate,
          workstream_metric_link: nextScopedLink,
          workstream_metric_links: nextLinkCollection,
        };
      };

      const applyMetricUpdateToTree = (
        cachedNode: unknown,
        targetWorkstreamId: number
      ): unknown => {
        if (!cachedNode || typeof cachedNode !== 'object') {
          return cachedNode;
        }

        let didChange = false;
        const workstreamNode = cachedNode as Workstream & {
          dependencies?: Array<{
            dependent?: Workstream | null;
          }> | null;
        };

        let nextNode: typeof cachedNode = cachedNode;

        if (Array.isArray(workstreamNode.metrics)) {
          const nextMetrics = workstreamNode.metrics.map((metric) => {
            if (metric.id !== metricId) {
              return metric;
            }

            didChange = true;
            return buildOptimisticMetricForWorkstream(metric, targetWorkstreamId);
          });

          if (didChange) {
            nextNode = {...(nextNode as object), metrics: nextMetrics};
          }
        }

        if (Array.isArray(workstreamNode.dependencies)) {
          let dependenciesChanged = false;
          const nextDependencies = workstreamNode.dependencies.map((dependency) => {
            if (!dependency?.dependent) {
              return dependency;
            }

            const nextDependent = applyMetricUpdateToTree(
              dependency.dependent,
              dependency.dependent.id
            ) as Workstream;
            if (nextDependent === dependency.dependent) {
              return dependency;
            }

            dependenciesChanged = true;
            return {...dependency, dependent: nextDependent};
          });

          if (dependenciesChanged) {
            didChange = true;
            nextNode = {...(nextNode as object), dependencies: nextDependencies};
          }
        }

        return didChange ? nextNode : cachedNode;
      };

      const applyMetricUpdate = (cachedWorkstream: unknown) => {
        if (!cachedWorkstream || typeof cachedWorkstream !== 'object') {
          return cachedWorkstream;
        }

        const cachedWorkstreamId =
          typeof (cachedWorkstream as Workstream).id === 'number'
            ? (cachedWorkstream as Workstream).id
            : originWorkstreamId;
        return applyMetricUpdateToTree(cachedWorkstream, cachedWorkstreamId);
      };

      const applyMetricUpdateToPaginatedWorkstreams = (cachedData: unknown) => {
        if (
          !cachedData ||
          typeof cachedData !== 'object' ||
          !Array.isArray((cachedData as {pages?: unknown[]}).pages)
        ) {
          return cachedData;
        }

        let didChange = false;
        const paginatedData = cachedData as {
          pages: Array<{
            workstreams?: unknown[];
          }>;
        };
        const nextPages = paginatedData.pages.map((page) => {
          if (!Array.isArray(page.workstreams)) {
            return page;
          }

          let pageChanged = false;
          const nextWorkstreams = page.workstreams.map((workstream) => {
            const nextWorkstream = applyMetricUpdate(workstream);
            if (nextWorkstream !== workstream) {
              pageChanged = true;
            }
            return nextWorkstream;
          });

          if (!pageChanged) {
            return page;
          }

          didChange = true;
          return {
            ...page,
            workstreams: nextWorkstreams,
          };
        });

        return didChange
          ? {
              ...paginatedData,
              pages: nextPages,
            }
          : cachedData;
      };

      return workstreamIds.flatMap((workstreamId) => {
        const previousWorkstreamQueries = queryClient.getQueriesData({
          queryKey: ['workstream', workstreamId],
        });
        queryClient.setQueriesData({queryKey: ['workstream', workstreamId]}, applyMetricUpdate);

        const workstreamDetailQueryKey = [
          'workstream-data',
          'workstream-detail',
          workstreamId,
        ] as const;
        const previousWorkstreamDetail = queryClient.getQueryData(workstreamDetailQueryKey);
        queryClient.setQueryData(workstreamDetailQueryKey, applyMetricUpdate);

        const previousWorkstreamListQueries = queryClient.getQueriesData({
          queryKey: WORKSTREAM_LIST_QUERY_PREFIX,
          exact: false,
        });
        for (const [queryKey] of previousWorkstreamListQueries) {
          queryClient.setQueryData(queryKey, applyMetricUpdateToPaginatedWorkstreams);
        }

        return [
          ...previousWorkstreamQueries,
          [workstreamDetailQueryKey, previousWorkstreamDetail] as const,
          ...previousWorkstreamListQueries,
        ];
      });
    },
    []
  );

  const invalidateMetricQueries = useCallback(
    (workstreamIds: number[]) => {
      const idsToInvalidate = normalizeMetricInvalidationIds(workstreamIds);

      queryClient.invalidateQueries({queryKey: ['workstreams'], exact: true});
      queryClient.invalidateQueries({queryKey: ['workstream-data'], exact: false});
      idsToInvalidate.forEach((workstreamId) => {
        void invalidateQueriesForWorkstreamId(workstreamId);
        queryClient.invalidateQueries({
          queryKey: ['workstream-data', 'workstream-detail', workstreamId],
        });
      });
    },
    [normalizeMetricInvalidationIds]
  );

  const {mutateAsync: createMetricAsync, isPending: isCreating} = useMutation({
    mutationFn: ({
      workstreamId,
      body,
      reason,
      skipAuditLog,
    }: {
      workstreamId: number;
      body: WorkstreamMetricCreate;
      reason?: string | null;
      skipAuditLog?: boolean;
    }) =>
      getService(WorkstreamService).createMetricApiV1WorkstreamIdMetricsPost(
        workstreamId,
        body,
        reason ?? undefined,
        skipAuditLog ?? false
      ),
    onSettled: (_, error, {workstreamId}) => {
      if (invalidateQueries) {
        invalidateMetricQueries([workstreamId]);
        if (!error) {
          void invalidateNotificationQueries();
        }
      }
    },
  });

  const {mutateAsync: updateMetricAsync, isPending: isUpdating} = useMutation({
    mutationFn: ({
      workstreamId,
      metricId,
      body,
      reason,
      skipAuditLog,
    }: {
      workstreamId: number;
      metricId: number;
      body: WorkstreamMetricEdit;
      reason?: string | null;
      skipAuditLog?: boolean;
      linkedWorkstreamIds: number[];
    }) =>
      getService(WorkstreamService).updateMetricApiV1WorkstreamIdMetricsMetricIdPut(
        workstreamId,
        metricId,
        body,
        reason ?? undefined,
        skipAuditLog ?? false
      ),
    onMutate: async ({workstreamId, metricId, body, linkedWorkstreamIds}) => {
      await Promise.all(
        linkedWorkstreamIds.map(async (linkedWorkstreamId) => {
          await queryClient.cancelQueries({queryKey: ['workstream', linkedWorkstreamId]});
          await queryClient.cancelQueries({
            queryKey: ['workstream-data', 'workstream-detail', linkedWorkstreamId],
          });
        })
      );
      await queryClient.cancelQueries({
        queryKey: WORKSTREAM_LIST_QUERY_PREFIX,
        exact: false,
      });

      return {
        previousQueryData: updateMetricInCachedWorkstream(
          linkedWorkstreamIds,
          workstreamId,
          metricId,
          body
        ),
      };
    },
    onError: (_error, _variables, context) => {
      context?.previousQueryData?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: (_, error, {linkedWorkstreamIds}) => {
      if (invalidateQueries) {
        invalidateMetricQueries(linkedWorkstreamIds);
        if (!error) {
          void invalidateNotificationQueries();
        }
      }
    },
  });

  const {mutateAsync: deleteMetricAsync, isPending: isDeleting} = useMutation({
    mutationFn: ({
      workstreamId,
      metricId,
      reason,
      skipAuditLog,
    }: {
      workstreamId: number;
      metricId: number;
      reason?: string | null;
      skipAuditLog?: boolean;
    }) =>
      getService(WorkstreamService).deleteMetricApiV1WorkstreamIdMetricsMetricIdDelete(
        workstreamId,
        metricId,
        reason ?? undefined,
        skipAuditLog ?? false
      ),
    onSettled: (_, error, {workstreamId}) => {
      if (invalidateQueries) {
        invalidateMetricQueries([workstreamId]);
        if (!error) {
          void invalidateNotificationQueries();
        }
      }
    },
  });

  const {mutateAsync: attachMetricAsync, isPending: isAttaching} = useMutation({
    mutationFn: ({
      workstreamId,
      body,
      reason,
      skipAuditLog,
    }: {
      workstreamId: number;
      body: MetricAttachRequest;
      reason?: string | null;
      skipAuditLog?: boolean;
    }) =>
      getService(WorkstreamService).attachMetricApiV1WorkstreamIdMetricsAttachPost(
        workstreamId,
        body,
        reason ?? undefined,
        skipAuditLog ?? false
      ),
    onSettled: (response, error, {workstreamId}) => {
      if (invalidateQueries) {
        invalidateMetricQueries([
          workstreamId,
          ...((response && typeof response === 'object'
            ? getMetricLinkedWorkstreamIds(response as WorkstreamMetric)
            : []) as number[]),
        ]);
        if (!error) {
          void invalidateNotificationQueries();
        }
      }
    },
  });

  const createMetric = useCallback(
    (
      workstreamId: number,
      update: Partial<WorkstreamMetric>,
      order: number,
      reason?: string | null,
      skipAuditLog?: boolean
    ) =>
      createMetricAsync({
        workstreamId,
        body: toMetricCreateBody(update, order),
        reason,
        skipAuditLog,
      }),
    [createMetricAsync]
  );

  const updateMetric = useCallback(
    (
      workstreamId: number,
      metricId: number,
      existing: WorkstreamMetric,
      update: WorkstreamMetricRequestUpdate,
      reason?: string | null,
      skipAuditLog?: boolean
    ) => {
      const effectiveExistingMetric = resolveMetricEditBase(workstreamId, metricId, existing);
      return updateMetricAsync({
        workstreamId,
        metricId,
        body: toMetricEditBody(effectiveExistingMetric, update, workstreamId),
        reason,
        skipAuditLog,
        linkedWorkstreamIds: normalizeMetricInvalidationIds([
          workstreamId,
          ...getMetricLinkedWorkstreamIds(effectiveExistingMetric),
          ...getAncestorWorkstreamIdsForDependency(workstreamId),
        ]),
      });
    },
    [normalizeMetricInvalidationIds, updateMetricAsync]
  );

  const deleteMetric = useCallback(
    (
      {workstreamId, metricId}: {workstreamId: number; metricId: number},
      reason?: string | null,
      skipAuditLog?: boolean
    ) =>
      deleteMetricAsync({
        workstreamId,
        metricId,
        reason,
        skipAuditLog,
      }),
    [deleteMetricAsync]
  );

  const attachMetric = useCallback(
    (
      workstreamId: number,
      attachRequest: MetricAttachRequest,
      reason?: string | null,
      skipAuditLog?: boolean
    ) =>
      attachMetricAsync({
        workstreamId,
        body: attachRequest,
        reason,
        skipAuditLog,
      }),
    [attachMetricAsync]
  );

  return {
    createMetric,
    updateMetric,
    deleteMetric,
    attachMetric,
    isPending: isCreating || isUpdating || isDeleting || isAttaching,
  };
};

export const useWorkstreamUndelete = ({onSuccess}: {onSuccess?: () => void} = {}) => {
  const {mutateAsync: undeleteWorkstream, isPending} = useMutation({
    mutationFn: (id: number) =>
      getService(WorkstreamService).undeleteApiV1WorkstreamIdUnarchivePost(id),
    onSettled: (response, _, id) => {
      if (!response) {
        return;
      }

      void invalidateQueriesForWorkstreamId(id);
      void invalidateNotificationQueries();

      onSuccess?.();
    },
  });

  return {undeleteWorkstream, isPending};
};

export const useWorkstreamPlanningDocs = (workstreamId?: number) => {
  const {data: planningDocs = [], isLoading} = useQuery({
    queryKey: ['workstream', 'planning', workstreamId],
    queryFn: () =>
      workstreamId
        ? getService(WorkstreamService).getEditordocsApiV1WorkstreamEditordocsGet(workstreamId)
        : Promise.resolve([] as WorkstreamEditorDocs[]),
    enabled: !!workstreamId,
  });

  const hasPlanningDocs = useMemo(() => {
    return planningDocs.length > 0;
  }, [planningDocs]);

  return {planningDocs, isLoading, hasPlanningDocs};
};

export const useCreateSwitchboard = (workstreamId: number) => {
  return useMutation({
    mutationFn: () =>
      getService(WorkstreamService).createSwitchboardApiV1WorkstreamSwitchboardPut(workstreamId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workstream', workstreamId, undefined, undefined],
      });
    },
  });
};

export const useMnpiAccess = () => {
  const {data: hasMnpiAccess, isLoading} = useQuery({
    queryKey: ['mnpi', 'access'],
    queryFn: () => getService(WorkstreamService).checkMnpiAccessApiV1WorkstreamMnpiCheckGet(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return {hasMnpiAccess: hasMnpiAccess ?? false, isLoading};
};
